import MainModel from "../../../components/Models/MainModel";
import { motion } from "framer-motion";
import Image from "../../../components/images/Image";
import Button from "../../../components/buttons/Button";
import { getFirefoxCssFix } from "../../../components/commonfunctions/getFirefoxCssFix";
import moment from "moment";

interface TransactionDetails {
  [key: string]: any; // Allows multiple key-value pairs of any type
}

interface ETopUpTransactionReceiptProps {
  details: TransactionDetails;
  handleClickDone: () => void;
  defaultPaymentType?: string;
  advance?: boolean;
  recharge?: boolean;
}

export default function ETopUpTransactionReceipt({
  details,
  handleClickDone,
  defaultPaymentType,
  advance,
  recharge
}: ETopUpTransactionReceiptProps) {
  const imageVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };
  const headingVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 },
  };

  // Parse and prepare recharge data if it's a recharge transaction
  const prepareRechargeData = () => {
    if (!recharge || !details) return {};
    
    // Create a flattened structure for display in the receipt
    return {
      date: details.transaction_details?.date || "-",
      transaction_id: details.transaction_details?.id || "-",
      type_of_transaction: details.transaction_details?.type || "Recharge",
      phone_number: `${details.country_code || ""} ${
        details.phone_number || ""
      }`,
      amount: details.amount || 0,
      mno: details.mno_details?.name || "-",
      sender_code_name: `${details.user_details?.user_code || ""} - ${
        details.user_details?.name || ""
      }`,
      transaction_status: details.transaction_details?.status || "-",
      sender_location: details.user_details?.location || "-",
      wallet_balance: details.wallet_balance || "-", // If available in the response
    };
  };

  // Use appropriate data source based on transaction type
  const displayData = recharge ? prepareRechargeData() : details;

  // Define transaction details in the exact order for display
  const transactionDetails = advance
  ? [
      "Date",
      "Transaction ID",
      "Type of Transaction",
      "Requested Amount",
      "Recipient User Code",
      "Recipient Location",
      "Sender User Code",
      "Sender Location",
    ]
  : recharge ? [
      "Date",
      "Transaction ID",
      "Type of Transaction",
      "Phone Number",
      "Amount",
      "MNO",
      "Sender Code & Name",
      "Transaction Status",
      "Sender Location",
      "E Top up Balance",
  ] : [
      "Date",
      "Transaction ID",
      "Type of Transaction",
      "Requested Amount",
      "Confirmed Amount",
      "Due Amount",
      "E-Topup Stock Added",
      "Variance",
      "Recipient User Code",
      "Recipient Location",
      "Sender User Code",
      "Sender Location",
      "Payment Source",
    ];

  const getDisplayValue = (label: string) => {
    if (recharge) {
      // Handle recharge specific fields
      switch (label) {
        case "Date":
          return moment(displayData.date).format("DD-MM-YYYY, hh:mm A");
        case "Transaction ID":
          return displayData.transaction_id || "-";
        case "Type of Transaction":
          return displayData.type_of_transaction || "Recharge";
        case "Phone Number":
          return displayData.phone_number || "-";
        case "Amount":
          return `AFA ${Number(displayData.amount).toFixed(2)}`;
        case "MNO":
          return displayData.mno || "-";
        case "Sender Code & Name":
          return displayData.sender_code_name || "-";
        case "Transaction Status":
          return displayData.transaction_status || "-";
        case "Sender Location":
          return displayData.sender_location || "-";
        case "E Top up Balance":
          return displayData.wallet_balance !== '-' ? ("AFA " + displayData.wallet_balance) : "-";
        default:
          return "-";
      }
    } else {
      // Original logic for non-recharge transactions
      switch (label) {
        case "Date":
          return moment(details.date).format("DD-MM-YYYY, hh:mm A");
        case "Requested Amount":
          return `AFA ${Number(details.requested_amount).toFixed(2)}`;
        case "Confirmed Amount":
          return `AFA ${Number(details.confirmed_amount).toFixed(2)}`;
        case "E-Topup Stock Added":
          return `AFA ${Number(details.e_topup_stock_added).toFixed(2)}`;
        case "Variance":
          return `AFA ${Number(details.variance).toFixed(2)}`;
        case "Due Amount":
          return `AFA ${Number(details.due_amount).toFixed(2)}`;
        case "Payment Source":
          return details.payment_source ?? "-";
        default:
          const fieldKey = label.toLowerCase().replace(/ /g, "_");
          return details[fieldKey] ?? "-";
      }
    }
  };

  const getPageTitle = () => {
    if (recharge) return 'Recharge Transaction Successful';
    if (advance) return 'E Top Up Stock Transferred Successfully';
    return 'E Top Up Request Approved Successfully';
  };

  return (
    <MainModel handleClose={handleClickDone}>
      <motion.div
        variants={imageVariants}
        initial="hidden"
        animate="visible"
        transition={{ duration: 0.4 }}
        className="flex items-center justify-center"
      >
        <Image src={"success_1"} className="" />
      </motion.div>

      <motion.h1
        variants={headingVariants}
        initial="hidden"
        animate="visible"
        transition={{ duration: 0.5, ease: "easeInOut", delay: 0.2 }}
        className="text-primary_text text-heading3 text-center pt-6"
      >
        {getPageTitle()}
      </motion.h1>

      <div className="h-transactionPopupHight">
        <div
          className={`h-full border mt-8 rounded-lg overflow-x-hidden overflow-auto ${getFirefoxCssFix(
            "scroll"
          )}`}
        >
          {transactionDetails.map((label) => (
            <div key={label} className="flex justify-between p-4 border-b">
              <p className="text-title2">{label}</p>
              <p className="text-title1">{getDisplayValue(label)}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4">
        <Button
          buttonName={"Done"}
          className={`py-2.5 rounded-[6px] cursor-pointer`}
          textSize={"text-large"}
          buttonOnclick={handleClickDone}
          testId={`done`}
          loading={false}
          buttonWidth={"w-[100px] cursor-pointer"}
          buttonHight="h-[42px]"
        />
      </div>
    </MainModel>
  );
}